"use client";

import { type CSSProperties, useEffect, useRef, useState } from "react";

import { cn } from "@/utils/cn";

type PreviewCompany = {
  name: string;
  brandName?: string;
  tierKo: string;
  customer: string;
  domainKo: string;
  stage?: string;
  employeeCount?: number;
  isHiring?: boolean;
  description?: string;
};

type PreviewCandidate = {
  id: string;
  name: string;
  photoUrl: string;
  headline: string;
  positions: PreviewCompany[];
  score: number;
  prevScore: number;
  rank: number;
};

const PREVIEW_TRAFFIC_REST = 3;
const PREVIEW_TRAFFIC_MAX = 5;
const PREVIEW_STRIDE = 86;
const SPOTLIGHT_ID = "p-coupang";
const PREVIEW_FRAMES = [
  { weight: 3, companyOpen: false, hold: 1700, caption: "평가 요건을 직접 조절해보세요" },
  { weight: 4, companyOpen: false, hold: 480, caption: "‘대규모 트래픽 처리’를 올리면 →" },
  { weight: 5, companyOpen: false, hold: 2300, caption: "후보 순위가 살아 움직입니다" },
  { weight: 5, companyOpen: true, hold: 2300, caption: "회사 맥락까지, 우리는 압니다" },
];

const PREVIEW_SEEDS = [
  {
    id: "p-toss",
    name: "홍길동",
    photoUrl: "https://randomuser.me/api/portraits/men/32.jpg",
    headline: "Senior Backend Engineer · 결제 도메인 6년",
    base: 92,
    trafficWeight: 0.5,
    positions: [
      {
        name: "토스",
        brandName: "토스",
        tierKo: "유니콘+",
        customer: "b2c",
        domainKo: "핀테크",
        stage: "Series F",
        employeeCount: 1898,
        isHiring: true,
        description: "모바일 금융 종합 서비스 운영",
      },
      {
        name: "위메프",
        tierKo: "중견",
        customer: "b2c",
        domainKo: "이커머스",
        stage: "Series C+",
        employeeCount: 850,
        description: "오픈마켓 커머스 플랫폼",
      },
    ],
  },
  {
    id: "p-banksalad",
    name: "김민재",
    photoUrl: "https://randomuser.me/api/portraits/men/45.jpg",
    headline: "Backend Engineer · 시리즈 C 핀테크",
    base: 87,
    trafficWeight: -1.3,
    positions: [
      {
        name: "뱅크샐러드",
        tierKo: "유니콘",
        customer: "b2c",
        domainKo: "핀테크 자산관리",
        stage: "Series D",
        employeeCount: 350,
        isHiring: true,
        description: "AI 기반 개인 자산관리 서비스",
      },
      {
        name: "카카오뱅크",
        tierKo: "대기업",
        customer: "b2c",
        domainKo: "인터넷전문은행",
        stage: "IPO",
        employeeCount: 1450,
        description: "국내 1호 인터넷전문은행",
      },
    ],
  },
  {
    id: SPOTLIGHT_ID,
    name: "이수민",
    photoUrl: "https://randomuser.me/api/portraits/women/22.jpg",
    headline: "Backend Tech Lead · 대규모 트래픽 4년",
    base: 84,
    trafficWeight: 3.4,
    positions: [
      {
        name: "쿠팡",
        tierKo: "대기업",
        customer: "b2c",
        domainKo: "이커머스·풀필먼트",
        stage: "IPO",
        employeeCount: 8000,
        isHiring: true,
        description: "이커머스·풀필먼트 통합 서비스",
      },
      {
        name: "우아한형제들",
        brandName: "배달의민족",
        tierKo: "대기업",
        customer: "b2c",
        domainKo: "음식배달",
        stage: "Series H",
        employeeCount: 2500,
        description: "음식배달 플랫폼",
      },
    ],
  },
] satisfies Array<{
  id: string;
  name: string;
  photoUrl: string;
  headline: string;
  base: number;
  trafficWeight: number;
  positions: PreviewCompany[];
}>;

function scoreAt(seed: (typeof PREVIEW_SEEDS)[number], weight: number) {
  const score = seed.base + seed.trafficWeight * (weight - PREVIEW_TRAFFIC_REST);
  return Math.max(0, Math.min(99, Math.round(score)));
}

function buildPreviewCandidates(weight: number): PreviewCandidate[] {
  return PREVIEW_SEEDS.map((seed) => ({
    id: seed.id,
    name: seed.name,
    photoUrl: seed.photoUrl,
    headline: seed.headline,
    positions: seed.positions,
    score: scoreAt(seed, weight),
    prevScore: scoreAt(seed, PREVIEW_TRAFFIC_REST),
    rank: 0,
  }))
    .sort((a, b) => b.score - a.score)
    .map((candidate, index) => ({ ...candidate, rank: index + 1 }));
}

function maskName(name: string) {
  return name ? `${name[0]}OO` : "";
}

export function HomeWowPreview() {
  const [weight, setWeight] = useState(PREVIEW_TRAFFIC_REST);
  const [frameIndex, setFrameIndex] = useState(0);
  const [interactive, setInteractive] = useState(false);
  const [inView, setInView] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [hidden, setHidden] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mediaQuery.matches);
    apply();
    mediaQuery.addEventListener?.("change", apply);
    return () => mediaQuery.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    const handleVisibility = () => setHidden(document.visibilityState === "hidden");
    handleVisibility();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.4 });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const active = inView && !hidden && !interactive && !reduced;

  useEffect(() => {
    if (!active) return;
    const frame = PREVIEW_FRAMES[frameIndex];
    setWeight(frame.weight);
    const timer = window.setTimeout(() => {
      setFrameIndex((current) => (current + 1) % PREVIEW_FRAMES.length);
    }, frame.hold);
    return () => window.clearTimeout(timer);
  }, [active, frameIndex]);

  function setWeightManual(nextWeight: number) {
    setInteractive(true);
    setWeight(nextWeight);
  }

  const rankedCandidates = buildPreviewCandidates(weight);
  const stableCandidates = [...rankedCandidates].sort((a, b) => a.id.localeCompare(b.id));
  const companyOpenId = active && PREVIEW_FRAMES[frameIndex].companyOpen ? SPOTLIGHT_ID : null;
  const caption = interactive
    ? "가중치를 바꾸면 순위가 다시 계산됩니다"
    : reduced
      ? "‘대규모 트래픽 처리’ 가중치를 바꿔보세요"
      : PREVIEW_FRAMES[frameIndex].caption;

  return (
    <div ref={containerRef} className="trial-glass-subtle w-full rounded-[26px] p-4" aria-label="서치라이트AI 작동 예시 미리보기">
      <div className="mb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(79,109,245,0.1)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--trial-brand)]">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--trial-brand)]" />
          예시 미리보기
        </span>
        <span className="text-[11px] text-[var(--trial-muted)]">실시간 인터랙티브 데모</span>
      </div>

      <div className="mb-1 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--trial-muted-strong)]">
            핀테크·결제 경험
            <span className="rounded bg-[rgba(79,109,245,0.1)] px-1 py-0.5 text-[10px] font-semibold text-[var(--trial-brand)]">
              필수
            </span>
          </span>
          <Dots value={5} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="shrink-0 text-[12.5px] text-[var(--trial-muted-strong)]">대규모 트래픽 처리</span>
          <WeightSlider value={weight} onChange={setWeightManual} />
        </div>
      </div>

      <div className="relative mt-3" style={{ height: stableCandidates.length * PREVIEW_STRIDE - 6 }}>
        {stableCandidates.map((candidate) => (
          <div
            key={candidate.id}
            className={cn(
              "absolute inset-x-0 transition-transform duration-[650ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
              companyOpenId === candidate.id ? "z-50" : "z-0",
            )}
            style={{ transform: `translateY(${(candidate.rank - 1) * PREVIEW_STRIDE}px)` }}
          >
            <PreviewCard
              candidate={candidate}
              companyOpen={companyOpenId === candidate.id}
              onInteract={() => setInteractive(true)}
            />
          </div>
        ))}
      </div>

      <div className="mt-3 h-5 text-center">
        <span key={caption} className="trial-fade-in inline-block text-[12.5px] font-medium text-[var(--trial-muted-strong)]">
          {caption}
        </span>
      </div>
      <p className="mt-2 text-center text-[11px] leading-relaxed text-[var(--trial-muted)]">
        예시 미리보기입니다 · 실제로는 입력하신 JD 기준으로 평가합니다
      </p>
    </div>
  );
}

function Dots({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden>
      {[1, 2, 3, 4, 5].map((item) => (
        <span key={item} className={cn("h-1.5 w-1.5 rounded-full", item <= value ? "bg-[var(--trial-brand)]" : "bg-white/70")} />
      ))}
    </span>
  );
}

function WeightSlider({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  const percent = ((value - 1) / (PREVIEW_TRAFFIC_MAX - 1)) * 100;

  return (
    <div className="flex w-[150px] items-center gap-3 tablet:w-[180px]">
      <input
        type="range"
        min={1}
        max={PREVIEW_TRAFFIC_MAX}
        step={1}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="trial-brand-range flex-1"
        style={{ "--val": `${percent}%` } as CSSProperties}
      />
      <span className="w-9 shrink-0 text-right font-mono text-[13px] text-[var(--trial-muted)]">
        <span className="font-semibold text-[var(--trial-brand)]">{value}</span> / 5
      </span>
    </div>
  );
}

function CompanyChip({
  company,
  current,
  open,
  onInteract,
}: {
  company: PreviewCompany;
  current: boolean;
  open: boolean;
  onInteract: () => void;
}) {
  const [hover, setHover] = useState(false);
  const shown = open || hover;

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => {
        setHover(true);
        onInteract();
      }}
      onMouseLeave={() => setHover(false)}
    >
      <span
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] transition-colors",
          current
            ? "bg-[rgba(79,109,245,0.1)] font-medium text-[var(--trial-brand)]"
            : "bg-white/55 text-[var(--trial-muted-strong)]",
          shown && "ring-1 ring-[rgba(79,109,245,0.22)]",
        )}
      >
        {company.brandName || company.name}
        {current ? <span className="text-[11px] opacity-70">· 현직</span> : null}
      </span>
      {shown ? (
        <span className="trial-fade-in absolute left-0 top-full z-50 mt-2 block w-[244px] rounded-lg border border-white/70 bg-white p-3 text-left shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
          <span className="mb-1 block text-[13.5px] font-bold leading-tight">
            {company.brandName ? `${company.brandName} (${company.name})` : company.name}
          </span>
          {company.description ? (
            <span className="mb-2 block text-[12px] text-[var(--trial-muted)]">{company.description}</span>
          ) : null}
          <span className="mb-1.5 flex flex-wrap gap-1">
            <span className="rounded bg-[rgba(79,109,245,0.1)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--trial-brand)]">
              {company.tierKo}
            </span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-[var(--trial-muted)]">
              {company.customer.toUpperCase()}
            </span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-[var(--trial-muted)]">
              {company.domainKo}
            </span>
          </span>
          <span className="block font-mono text-[11.5px] text-[var(--trial-muted)]">
            {company.stage ? `${company.stage} · ` : ""}
            {company.employeeCount ? `직원 ${company.employeeCount.toLocaleString()}명` : ""}
            {company.isHiring ? " · 채용중" : ""}
          </span>
        </span>
      ) : null}
    </span>
  );
}

function PreviewCard({
  candidate,
  companyOpen,
  onInteract,
}: {
  candidate: PreviewCandidate;
  companyOpen: boolean;
  onInteract: () => void;
}) {
  const delta = candidate.score - candidate.prevScore;

  return (
    <div className="trial-glass-strong trial-lift flex h-[80px] cursor-pointer items-center gap-3 rounded-2xl px-3.5">
      <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-white/60">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={candidate.photoUrl} alt="" className="h-full w-full object-cover" style={{ filter: "blur(7px)" }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5">
          <span className="text-[14.5px] font-bold leading-none text-[var(--trial-foreground)]">{maskName(candidate.name)}</span>
          <span className="inline-flex items-center rounded bg-[rgba(79,109,245,0.1)] px-1.5 py-0.5 text-[11px] font-semibold text-[var(--trial-brand)]">
            Top {candidate.rank}
          </span>
        </div>
        <div className="mb-1.5 truncate text-[12px] leading-snug text-[var(--trial-muted)]">{candidate.headline}</div>
        <div className="flex items-center gap-1">
          {candidate.positions.map((position, index) => (
            <div key={`${candidate.id}-${position.name}`} className="flex items-center gap-1">
              <CompanyChip company={position} current={index === 0} open={companyOpen && index === 0} onInteract={onInteract} />
              {index < candidate.positions.length - 1 ? <span className="text-[11px] text-[var(--trial-muted)]">→</span> : null}
            </div>
          ))}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="font-mono text-[26px] font-bold leading-none text-[var(--trial-brand)]">{candidate.score}</div>
        {delta !== 0 ? (
          <div className={cn("mt-0.5 font-mono text-[11.5px]", delta > 0 ? "text-[var(--trial-success)]" : "text-[#ef4444]")}>
            {delta > 0 ? "↑" : "↓"} {candidate.prevScore}→{candidate.score}
          </div>
        ) : (
          <div className="mt-0.5 text-[10.5px] text-[var(--trial-muted)]">매칭</div>
        )}
      </div>
    </div>
  );
}
